import { z } from 'zod';

/**
 * Hak akses panel admin.
 *
 * SUPER_ADMIN selalu punya akses penuh dan tidak bisa dibatasi, supaya tidak
 * ada yang bisa mengunci semua orang keluar. Akses ADMIN dan STAFF diatur
 * super admin per menu; menu yang belum pernah diatur memakai DEFAULT_ACCESS.
 */

export const ADMIN_ROLES = ['SUPER_ADMIN', 'ADMIN', 'STAFF'] as const;
export type AdminRole = (typeof ADMIN_ROLES)[number];

/** Role yang aksesnya bisa diatur. */
export const CONFIGURABLE_ROLES = ['ADMIN', 'STAFF'] as const;
export type ConfigurableRole = (typeof CONFIGURABLE_ROLES)[number];

export const ACCESS_LEVELS = ['NONE', 'VIEW', 'MANAGE'] as const;
export type AccessLevel = (typeof ACCESS_LEVELS)[number];

export interface AdminModuleDef {
  key: string;
  label: string;
  group: string;
  hint: string;
  /** false = hanya ada tingkat Lihat (mis. angka omzet tidak bisa "dikelola"). */
  manageable: boolean;
}

export const ADMIN_MODULES = [
  {
    key: 'dashboard',
    label: 'Dashboard',
    group: 'Ringkasan',
    hint: 'Antrean pesanan dan ringkasan katalog',
    manageable: false,
  },
  {
    key: 'revenue',
    label: 'Omzet',
    group: 'Ringkasan',
    hint: 'Angka omzet di dashboard',
    manageable: false,
  },
  {
    key: 'articles',
    label: 'Artikel',
    group: 'Konten',
    hint: 'Menulis dan menerbitkan artikel',
    manageable: true,
  },
  {
    key: 'media',
    label: 'Media',
    group: 'Konten',
    hint: 'Pustaka gambar; dibutuhkan untuk mengunggah foto produk dan artikel',
    manageable: true,
  },
  {
    key: 'banners',
    label: 'Banner',
    group: 'Konten',
    hint: 'Carousel promo dan event di beranda',
    manageable: true,
  },
  {
    key: 'pages',
    label: 'Halaman',
    group: 'Konten',
    hint: 'Halaman bebas di situs, termasuk menimpa Tentang/Kontak/Kebijakan',
    manageable: true,
  },
  {
    key: 'products',
    label: 'Produk',
    group: 'Katalog',
    hint: 'Detail, harga, opsi, dan foto produk',
    manageable: true,
  },
  {
    key: 'categories',
    label: 'Kategori',
    group: 'Katalog',
    hint: 'Pohon kategori katalog',
    manageable: true,
  },
  {
    key: 'services',
    label: 'Layanan',
    group: 'Katalog',
    hint: 'Halaman jasa di /layanan',
    manageable: true,
  },
  {
    key: 'studio',
    label: 'Imaji Studio',
    group: 'Katalog',
    hint: 'Kategori, template, dan font editor desain',
    manageable: true,
  },
  {
    key: 'orders',
    label: 'Pesanan',
    group: 'Transaksi',
    hint: 'Status, resi, catatan, unduh desain',
    manageable: true,
  },
  {
    key: 'customers',
    label: 'Pelanggan',
    group: 'Transaksi',
    hint: 'Akun, penangguhan, dan peran',
    manageable: true,
  },
  {
    key: 'assistant',
    label: 'Asisten AI',
    group: 'Bantuan',
    hint: 'Memakai Imma dan meninjau percakapan',
    manageable: true,
  },
  {
    key: 'knowledge',
    label: 'Knowledge Base',
    group: 'Bantuan',
    hint: 'Sumber pengetahuan Imma dan halaman FAQ',
    manageable: true,
  },
  {
    key: 'internalTopics',
    label: 'Topik Internal',
    group: 'Pengaturan',
    hint: 'Catatan yang perlu penyesuaian, data, atau keputusan tim CI',
    manageable: true,
  },
] as const satisfies readonly AdminModuleDef[];

export type AdminModule = (typeof ADMIN_MODULES)[number]['key'];
export const ADMIN_MODULE_KEYS = ADMIN_MODULES.map((m) => m.key);

export type AccessMap = Record<AdminModule, AccessLevel>;

export const DEFAULT_ACCESS: Record<ConfigurableRole, AccessMap> = {
  ADMIN: {
    dashboard: 'VIEW',
    revenue: 'VIEW',
    articles: 'MANAGE',
    media: 'MANAGE',
    banners: 'MANAGE',
    pages: 'MANAGE',
    products: 'MANAGE',
    categories: 'MANAGE',
    services: 'MANAGE',
    studio: 'MANAGE',
    orders: 'MANAGE',
    customers: 'MANAGE',
    assistant: 'MANAGE',
    knowledge: 'MANAGE',
    internalTopics: 'MANAGE',
  },
  STAFF: {
    dashboard: 'VIEW',
    revenue: 'NONE',
    articles: 'MANAGE',
    media: 'MANAGE',
    banners: 'MANAGE',
    pages: 'VIEW',
    products: 'VIEW',
    categories: 'VIEW',
    services: 'VIEW',
    studio: 'VIEW',
    orders: 'MANAGE',
    customers: 'VIEW',
    assistant: 'MANAGE',
    knowledge: 'MANAGE',
    internalTopics: 'VIEW',
  },
};

export const FULL_ACCESS: AccessMap = Object.fromEntries(
  ADMIN_MODULES.map((m) => [m.key, m.manageable ? 'MANAGE' : 'VIEW']),
) as AccessMap;

const RANK: Record<AccessLevel, number> = { NONE: 0, VIEW: 1, MANAGE: 2 };

export function hasAccess(granted: AccessLevel | undefined, required: AccessLevel): boolean {
  return RANK[granted ?? 'NONE'] >= RANK[required];
}

export function isAdminRole(role: string | null | undefined): role is AdminRole {
  return role === 'SUPER_ADMIN' || role === 'ADMIN' || role === 'STAFF';
}

/** SUPER_ADMIN dan ADMIN: dipakai untuk aturan yang dulu memeriksa role === 'ADMIN'. */
export function isAdminOrAbove(role: string | null | undefined): boolean {
  return role === 'SUPER_ADMIN' || role === 'ADMIN';
}

const ROLE_RANK: Record<string, number> = { CUSTOMER: 0, STAFF: 1, ADMIN: 2, SUPER_ADMIN: 3 };

export function roleRank(role: string): number {
  return ROLE_RANK[role] ?? 0;
}

/** Tingkat untuk menu yang tidak bisa dikelola dipotong ke VIEW. */
export function normalizeAccess(
  map: Partial<Record<string, AccessLevel>>,
  base: AccessMap,
): AccessMap {
  const out = { ...base };
  for (const mod of ADMIN_MODULES) {
    const level = map[mod.key];
    if (level === undefined) continue;
    out[mod.key] = !mod.manageable && level === 'MANAGE' ? 'VIEW' : level;
  }
  return out;
}

/** Role panel yang dibuat super admin; nama tampil apa adanya di tabel hak akses. */
export const adminRoleNameSchema = z
  .string()
  .trim()
  .min(2, 'Nama role minimal 2 karakter')
  .max(40, 'Nama role maksimal 40 karakter');

export const adminRoleCreateSchema = z.object({ name: adminRoleNameSchema });

export const adminRoleUpdateSchema = z
  .object({
    name: adminRoleNameSchema.optional(),
    /**
     * Cukup modul yang diubah; sisanya mengikuti nilai tersimpan. Kunci di luar
     * ADMIN_MODULES diabaikan normalizeAccess.
     */
    permissions: z.record(z.string().min(1).max(40), z.enum(ACCESS_LEVELS)).optional(),
  })
  .refine((data) => Object.keys(data).length > 0, { message: 'Tidak ada perubahan yang dikirim' });

export const adminRoleParamsSchema = z.object({ id: z.string().min(1).max(64) });

export type AdminRoleCreate = z.infer<typeof adminRoleCreateSchema>;
export type AdminRoleUpdate = z.infer<typeof adminRoleUpdateSchema>;

export const roleAccessUpdateSchema = z.object({
  permissions: z.record(
    z.enum(ADMIN_MODULE_KEYS as [AdminModule, ...AdminModule[]]),
    z.enum(ACCESS_LEVELS),
  ),
});

export const configurableRoleParamsSchema = z.object({
  role: z.enum(CONFIGURABLE_ROLES),
});

export type RoleAccessUpdate = z.infer<typeof roleAccessUpdateSchema>;
